import { useEffect, useCallback, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import _ from "lodash"

import config from "../../config"

import { fetchAppManifest } from "../../services/venue"
import { VenueContext } from "./VenueContext"
import { useTheme } from "./hooks/useTheme.ts"

import {
  prepareExtensionConfig,
  prepareFeatureQuickSearchOrderConfig,
  prepareMapConfig,
  prepareStepAutoplayConfig,
} from "./utils/prepareConfig"

import { RealtimeProvider } from "./modules/realtime"
import { useGetAppConfig } from "./modules/data"

export const defaultAppConfig = {
  map: {},
}

export const VenueProvider = ({ children }) => {
  // Modules
  /** State, Hooks */
  const device = config("device")
  const deviceType = /^kiosk/.test(device) ? "kiosk" : "mobile"

  /** 
   * Example Extension Configs
    {
      "extensions": {
          "app": [
            {
              "to": "promotions", // <-- path for registering route
              "component": "tourist-friendly/pages/promotions" // <-- component path for loading
            },
            ....
          ]
        }
      }
    }
  */
  const { data: appConfig, isFetched: isConfigFetched } = useGetAppConfig()

  const mapConfig = useMemo(
    () => prepareMapConfig(_.get(appConfig, "map")),
    [appConfig]
  )

  const stepAutoplayConfig = useMemo(
    () => prepareStepAutoplayConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const extensionConfig = useMemo(
    () => prepareExtensionConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const featureQuickSearchOrderConfig = useMemo(
    () => prepareFeatureQuickSearchOrderConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const { data: appManifest } = useQuery({
    queryKey: ["app-manifest"],
    queryFn: fetchAppManifest,
    retry: true,
    refetchOnWindowFocus: false,
  })

  const themeName = config("themeName")
  const mapThemeName = config("mapThemeName")
  const { themeOptions, initVenueTheme } = useTheme(themeName, mapThemeName)

  const initVenue = useCallback(async () => {
    initVenueTheme()
  }, [initVenueTheme])

  /**
   * Side Effect when manifest is updated
   */
  useEffect(() => {
    // Set favicon
    const favicon = appManifest?.icons?.find(
      (icon) => icon.type === "image/x-icon"
    )
    if (favicon) {
      document.getElementById("favicon").href = favicon.src
    }
  }, [appManifest])

  // fetch ข้อมูลเริ่มต้นลง feature state และ theme
  useEffect(() => {
    try {
      initVenue()
    } catch (err) {
      throw err
    }
  }, [initVenue])

  const value = {
    // Realtime
    themeOptions,
    appManifest,
    appConfig,
    stepAutoplayConfig,
    extensionConfig,
    featureQuickSearchOrderConfig,
    // Flag
    configLoaded: isConfigFetched,
    dataLoaded: isConfigFetched,
    mapConfig,
  }

  return (
    <VenueContext.Provider value={value}>
      <RealtimeProvider>{children}</RealtimeProvider>
    </VenueContext.Provider>
  )
}
